import { useRef, useState } from 'react';
import { useComments, useDeleteComment, useEditComment } from '@/hooks/useComments.js';
import { useEditorUpload } from '@/hooks/useIssues.js';
import type { RichEditorHandle } from '@/features/editor/RichEditor.js';
import { LazyRichEditor as RichEditor } from '@/features/editor/LazyRichEditor.js';
import { timeAgo } from '@/lib/time.js';
import { useAuth } from '@/lib/auth.js';
import { Avatar } from '@/components/Avatar.js';
import { AdfRenderer } from '@/components/AdfRenderer.js';
import type { JiraAttachment } from '@shared/types.js';

interface CommentListProps {
  issueKey: string;
  projectKey?: string;
  attachments?: JiraAttachment[];
  onReply?: (mention: { id: string; label: string }) => void;
}

// Slack alerts link to /issues/KEY#comment-ID; scroll there once the comment renders
let scrolledToHash = '';
function scrollToLinkedComment(el: HTMLDivElement | null) {
  if (!el || typeof window === 'undefined') return;
  const hash = window.location.hash;
  if (hash !== `#${el.id}` || scrolledToHash === hash) return;
  scrolledToHash = hash;
  // Images above the comment load after it renders and push it down; settle again until
  // the person scrolls themselves.
  let userScrolled = false;
  const stop = () => { userScrolled = true; };
  window.addEventListener('wheel', stop, { once: true, passive: true });
  window.addEventListener('touchmove', stop, { once: true, passive: true });
  for (const delay of [0, 400, 1200, 2500]) {
    setTimeout(() => {
      if (!userScrolled && el.isConnected) el.scrollIntoView({ behavior: delay ? 'auto' : 'smooth', block: 'center' });
    }, delay);
  }
}

interface CommentEditorProps {
  issueKey: string;
  projectKey?: string;
  commentId: string;
  initialAdf: unknown;
  attachments?: JiraAttachment[];
  onDone: () => void;
}

function CommentEditor({ issueKey, projectKey, commentId, initialAdf, attachments, onDone }: CommentEditorProps) {
  const editorRef = useRef<RichEditorHandle>(null);
  const [busy, setBusy] = useState(false);
  const editComment = useEditComment(issueKey);
  const upload = useEditorUpload(issueKey);

  const save = () => {
    const ed = editorRef.current;
    if (!ed || ed.isEmpty() || ed.isUploading() || editComment.isPending) return;
    editComment.mutate({ commentId, adf: ed.getAdf() }, { onSuccess: onDone });
  };

  return (
    <div className="mt-2">
      <RichEditor
        ref={editorRef}
        initialAdf={initialAdf}
        attachments={attachments}
        projectKey={projectKey}
        onUploadFile={upload}
        onSubmit={save}
        onChange={() => setBusy(!!editorRef.current?.isUploading())}
        autoFocus
      />
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={busy || editComment.isPending}
          className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {editComment.isPending ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={onDone} className="rounded-md px-3 py-1 text-sm text-gray-600 hover:bg-gray-100">
          Cancel
        </button>
        {editComment.isError && <span className="text-sm text-red-600">Failed to save. Please try again.</span>}
      </div>
    </div>
  );
}


export function CommentList({ issueKey, projectKey, attachments, onReply }: CommentListProps) {
  const { data: comments, isLoading } = useComments(issueKey);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { user } = useAuth();
  const deleteComment = useDeleteComment(issueKey);

  if (isLoading) {
    return <div className="py-4 text-center text-sm text-gray-500">Loading comments...</div>;
  }

  if (!comments || comments.length === 0) {
    return <div className="py-4 text-center text-sm text-gray-500">No comments yet</div>;
  }

  function handleDelete(commentId: string) {
    if (window.confirm('Delete this comment?')) {
      deleteComment.mutate(commentId);
    }
  }

  return (
    <div className="space-y-3">
      {comments.map((comment) => {
        // User can delete their own portal comments
        const canDelete = comment.author.isPortalUser && user?.name === comment.author.displayName;

        return (
          <div
            key={comment.id}
            id={`comment-${comment.id}`}
            ref={scrollToLinkedComment}
            className={`scroll-mt-20 rounded-lg border bg-white p-4 ${typeof window !== 'undefined' && window.location.hash === `#comment-${comment.id}` ? 'ring-2 ring-blue-300' : ''}`}
          >
            <div className="flex items-center gap-2">
              <Avatar
                name={comment.author.displayName}
                avatarUrl={comment.author.avatarUrl}
                size="sm"
              />
              <span className="text-sm font-medium text-gray-900">
                {comment.author.displayName}
              </span>
              {comment.author.isPortalUser && (
                <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                  Portal
                </span>
              )}
              <span className="text-xs text-gray-400">{timeAgo(comment.created)}</span>
              {comment.updated && comment.updated !== comment.created && (
                <span className="text-xs text-gray-400">(edited)</span>
              )}
              {onReply && comment.author.mentionId && editingId !== comment.id && (
                <button
                  onClick={() => onReply({ id: comment.author.mentionId!, label: comment.author.displayName })}
                  className="ml-auto text-xs text-gray-400 hover:text-blue-600"
                  title="Reply and mention the author"
                >
                  Reply
                </button>
              )}
              {canDelete && editingId !== comment.id && (
                <button
                  onClick={() => setEditingId(comment.id)}
                  className={`${onReply && comment.author.mentionId ? '' : 'ml-auto '}text-xs text-gray-400 hover:text-blue-600`}
                  title="Edit comment"
                >
                  Edit
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => handleDelete(comment.id)}
                  disabled={deleteComment.isPending}
                  className={`${editingId === comment.id ? 'ml-auto ' : ''}text-xs text-gray-400 hover:text-red-500 disabled:opacity-50`}
                  title="Delete comment"
                >
                  Delete
                </button>
              )}
            </div>
            {editingId === comment.id ? (
              <CommentEditor
                issueKey={issueKey}
                projectKey={projectKey}
                commentId={comment.id}
                initialAdf={comment.bodyAdf}
                attachments={attachments}
                onDone={() => setEditingId(null)}
              />
            ) : (
              <div className="mt-2">
                <AdfRenderer adf={comment.bodyAdf} fallbackText={comment.body} attachments={attachments} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
